import React, {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useState
} from 'react';
import { Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { twMerge } from 'tailwind-merge';

import { MenuMaster } from 'models/role';
import { MasterCompany } from 'models/company/MasterCompany';
import { useAdminCompanies } from 'hooks';

export type WorkflowMenuMasterFormRefHandle = {
    submit: () => MenuMaster[] | undefined;
};

export type WorkflowMenuMasterFormProps = {
    disabledKeyField?: boolean;
    defaultValues?: MenuMaster[];
    className?: string;
};

export const WorkflowMenuMasterForm = forwardRef<
    WorkflowMenuMasterFormRefHandle,
    WorkflowMenuMasterFormProps
>((props, ref) => {
    const { disabledKeyField, defaultValues, className } = props;

    const [formValues, setFormValues] = useState<
        Partial<Pick<MenuMaster, 'id' | 'menu_name'>>
    >(() => {
        const firstDefaultMenu = defaultValues?.[0];
        return {
            id: firstDefaultMenu?.id ?? '',
            menu_name: firstDefaultMenu?.menu_name ?? ''
        };
    });
    const [selectedSIDSet, setSelectedSIDSet] = useState<Set<string>>(
        new Set<string>(defaultValues?.map(menu => menu.sid) ?? [])
    );
    const [errors, setErrors] = useState<Record<keyof MenuMaster, string>>(
        {} as MenuMaster
    );

    const { adminCompanies, isAllowedAllCompanies } = useAdminCompanies();
    const adminCompanyOptions = useMemo(() => {
        if (!adminCompanies) {
            return undefined;
        }

        if (isAllowedAllCompanies) {
            const selectAllOption = {
                sid: '*',
                company_name: '*'
            } as MasterCompany;
            return [selectAllOption, ...adminCompanies];
        }

        return adminCompanies;
    }, [adminCompanies, isAllowedAllCompanies]);

    const handleFormValueChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const key = event.currentTarget.name;
        const value = event.currentTarget.value;

        setFormValues({ ...formValues, [key]: value });
    };

    const handleCompanyCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isCheck = event.target.checked;
        if (isCheck) {
            selectedSIDSet.add(event.target.id);
        } else {
            selectedSIDSet.delete(event.target.id);
        }

        setSelectedSIDSet(new Set(selectedSIDSet));
    };

    const submitForm = () => {
        const errors: [keyof MenuMaster, string][] = [];
        if (selectedSIDSet.size === 0) {
            errors.push(['sid', 'กรุณาเลือก Company']);
        }
        if (!formValues.id) {
            errors.push(['id', 'กรุณากรอก Menu ID']);
        }
        if (!formValues.menu_name) {
            errors.push(['menu_name', 'กรุณากรอกชื่อ']);
        }

        if (errors.length > 0) {
            setErrors(prevState => ({
                ...prevState,
                // @ts-ignore
                ...Object.fromEntries(errors)
            }));

            return undefined;
        }

        return Array.from<string, MenuMaster>(
            selectedSIDSet,
            sid =>
                ({
                    sid,
                    id: formValues.id,
                    menu_name: formValues.menu_name
                }) as MenuMaster
        );
    };

    useImperativeHandle(
        ref,
        () => ({
            submit: submitForm
        }),
        [submitForm]
    );

    return (
        <Form className={twMerge('flex flex-col', className)}>
            <FormGroup>
                <Label for="id">Menu ID</Label>
                <Input
                    disabled={disabledKeyField}
                    invalid={!!errors.id}
                    id="id"
                    type="text"
                    name="id"
                    value={formValues.id}
                    onChange={
                        !disabledKeyField ? handleFormValueChange : undefined
                    }
                />
                <FormFeedback>{errors.id}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="menu_name">Menu Name</Label>
                <Input
                    invalid={!!errors.menu_name}
                    id="menu_name"
                    type="text"
                    name="menu_name"
                    value={formValues.menu_name}
                    onChange={handleFormValueChange}
                />
                <FormFeedback>{errors.menu_name}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label>Company</Label>
                {adminCompanies?.map(adminCompany => (
                    <div
                        key={adminCompany.sid}
                        className="flex items-center gap-2"
                    >
                        <Input
                            id={adminCompany.sid}
                            type="checkbox"
                            defaultChecked={selectedSIDSet.has(
                                adminCompany.sid
                            )}
                            onChange={handleCompanyCheck}
                            className="static m-0"
                        />
                        <Label
                            for={adminCompany.sid}
                            className="m-0 py-0.5"
                        >
                            {adminCompany.company_name}
                        </Label>
                    </div>
                ))}
            </FormGroup>
        </Form>
    );
});

WorkflowMenuMasterForm.displayName = 'WorkflowMenuMasterForm';
